import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useApp } from '@/context/AppContext';
import { LayoutDashboard, Menu, X, LogIn } from 'lucide-react';

const CurrencyToggle = ({ currency, setCurrency }) => (
  <div
    className="flex items-center bg-secondary/50 border border-[color:var(--hairline)] rounded-lg overflow-hidden"
    data-testid="currency-toggle"
    role="group"
    aria-label="Currency selector"
  >
    <button
      onClick={() => setCurrency('PHP')}
      className={`px-2.5 py-1.5 text-[11px] font-bold transition-colors duration-150 ${
        currency === 'PHP' ? 'bg-brand-blue text-white' : 'text-muted-foreground hover:text-foreground'
      }`}
      data-testid="currency-php"
      aria-pressed={currency === 'PHP'}
    >
      {'₱'} PHP
    </button>
    <button
      onClick={() => setCurrency('USD')}
      className={`px-2.5 py-1.5 text-[11px] font-bold transition-colors duration-150 ${
        currency === 'USD' ? 'bg-brand-blue text-white' : 'text-muted-foreground hover:text-foreground'
      }`}
      data-testid="currency-usd"
      aria-pressed={currency === 'USD'}
    >
      $ USD
    </button>
  </div>
);

/* Nav link with subtle hover underline */
const NavLink = ({ href, to, onClick, children, testId, className = '' }) => {
  const baseClass = `relative text-sm font-medium text-muted-foreground hover:text-foreground transition-colors duration-200 after:absolute after:bottom-[-2px] after:left-0 after:h-[2px] after:w-0 after:bg-brand-blue after:transition-all after:duration-200 hover:after:w-full ${className}`;
  if (to) return <Link to={to} onClick={onClick} className={baseClass} data-testid={testId}>{children}</Link>;
  return <a href={href} onClick={onClick} className={baseClass} data-testid={testId}>{children}</a>;
};

export const Navbar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { activeOrder, currency, setCurrency } = useApp();
  const isLanding = location.pathname === '/';
  const [mobileOpen, setMobileOpen] = useState(false);
  const closeMobile = () => setMobileOpen(false);

  const goHome = (e) => {
    e.preventDefault();
    if (isLanding) window.scrollTo({ top: 0, behavior: 'smooth' });
    else navigate('/');
  };

  return (
    <motion.nav
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
      className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-[color:var(--hairline)]"
      data-testid="navbar"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-18 lg:h-20">

          {/* ── LEFT: Logo + Nav Links ── */}
          <div className="flex items-center gap-8 lg:gap-10">
            {/* Logo (acts as Home link) */}
            <a href="/" onClick={goHome} className="flex items-center shrink-0" data-testid="navbar-logo" aria-label="MediaLabs Home">
              <img
                src="/medialabs-logo.jpg"
                alt="MediaLabs I.T. Solutions"
                className="h-10 sm:h-12 lg:h-14 w-auto object-contain"
              />
            </a>

            {/* Desktop nav links */}
            <div className="hidden lg:flex items-center gap-7">
              {isLanding && (
                <>
                  <NavLink href="#how-it-works" testId="nav-link-how-it-works">How It Works</NavLink>
                  <NavLink href="#speed-advantage" testId="nav-link-speed-advantage">Speed Advantage</NavLink>
                  <NavLink href="#industries" testId="nav-link-industries">Industries</NavLink>
                </>
              )}
              {!isLanding && <NavLink href="/" onClick={goHome} testId="nav-link-home">Home</NavLink>}
              <NavLink to="/services" testId="nav-link-services">Services</NavLink>
              <NavLink to="/blog" testId="nav-link-blog">Blog</NavLink>
              {activeOrder && (
                <Link to="/dashboard" className="inline-flex items-center gap-1.5 text-sm font-medium text-brand-blue hover:text-brand-blue-dark transition-colors duration-200" data-testid="nav-link-dashboard">
                  <LayoutDashboard className="w-4 h-4" /> Dashboard
                </Link>
              )}
            </div>
          </div>

          {/* ── RIGHT: Utilities + CTAs ── */}
          <div className="hidden lg:flex items-center gap-3">
            <CurrencyToggle currency={currency} setCurrency={setCurrency} />
            <Link to="/auth" className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground border border-[color:var(--hairline)] rounded-xl px-3.5 py-2 hover:bg-secondary/60 transition-colors duration-200" data-testid="nav-client-login" aria-label="Client login portal">
              <LogIn className="w-4 h-4" /> Client Login
            </Link>
            <motion.div whileHover={{ y: -1 }} whileTap={{ scale: 0.98 }}>
              <Link to="/services" className="inline-flex items-center justify-center rounded-xl bg-brand-blue text-white text-sm font-semibold px-5 py-2.5 shadow-[var(--shadow-soft)] hover:shadow-[var(--shadow-lift)] hover:bg-brand-blue-dark transition-shadow transition-colors duration-200" data-testid="nav-cta-button">View Gigs</Link>
            </motion.div>
          </div>

          {/* ── MOBILE: Toggle ── */}
          <div className="flex lg:hidden items-center gap-2">
            <CurrencyToggle currency={currency} setCurrency={setCurrency} />
            <button onClick={() => setMobileOpen(!mobileOpen)} className="w-10 h-10 rounded-xl flex items-center justify-center hover:bg-secondary/60 transition-colors duration-200" data-testid="mobile-menu-toggle" aria-label="Toggle navigation menu">
              {mobileOpen ? <X className="w-5 h-5 text-foreground" /> : <Menu className="w-5 h-5 text-foreground" />}
            </button>
          </div>
        </div>
      </div>

      {/* ── MOBILE MENU ── */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.2 }} className="lg:hidden border-t border-[color:var(--hairline)] bg-white/95 backdrop-blur-md" data-testid="mobile-menu">
            <div className="max-w-6xl mx-auto px-4 py-4 space-y-1">
              {isLanding && (<>
                <a href="#how-it-works" onClick={closeMobile} className="block px-3 py-2.5 rounded-lg text-sm font-medium text-muted-foreground hover:bg-secondary/60 hover:text-foreground transition-colors">How It Works</a>
                <a href="#speed-advantage" onClick={closeMobile} className="block px-3 py-2.5 rounded-lg text-sm font-medium text-muted-foreground hover:bg-secondary/60 hover:text-foreground transition-colors">Speed Advantage</a>
                <a href="#industries" onClick={closeMobile} className="block px-3 py-2.5 rounded-lg text-sm font-medium text-muted-foreground hover:bg-secondary/60 hover:text-foreground transition-colors">Industries</a>
              </>)}
              {!isLanding && <a href="/" onClick={(e) => { closeMobile(); goHome(e); }} className="block px-3 py-2.5 rounded-lg text-sm font-medium text-muted-foreground hover:bg-secondary/60 hover:text-foreground transition-colors">Home</a>}
              <Link to="/services" onClick={closeMobile} className="block px-3 py-2.5 rounded-lg text-sm font-medium text-muted-foreground hover:bg-secondary/60 hover:text-foreground transition-colors" data-testid="mobile-link-services">Services</Link>
              <Link to="/blog" onClick={closeMobile} className="block px-3 py-2.5 rounded-lg text-sm font-medium text-muted-foreground hover:bg-secondary/60 hover:text-foreground transition-colors" data-testid="mobile-link-blog">Blog</Link>
              {activeOrder && <Link to="/dashboard" onClick={closeMobile} className="flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium text-brand-blue hover:bg-brand-blue/5 transition-colors" data-testid="mobile-link-dashboard"><LayoutDashboard className="w-4 h-4" /> Dashboard</Link>}
              <div className="border-t border-[color:var(--hairline)] my-2" />
              <Link to="/auth" onClick={closeMobile} className="flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-semibold text-foreground bg-secondary/40 hover:bg-secondary/70 transition-colors" data-testid="mobile-client-login"><LogIn className="w-4 h-4" /> Client Login</Link>
              <Link to="/services" onClick={closeMobile} className="block text-center py-2.5 rounded-xl bg-brand-blue text-white text-sm font-semibold shadow-[var(--shadow-soft)] hover:bg-brand-blue-dark transition-colors">View Gigs</Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
};
